import { useGetHeliosDataTablesQuery } from 'store/services/helios/dataHelios';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, MenuItem, TableCell as MuiTableCell, Typography } from '@mui/material';
import { type Control, Controller, type FieldValues, type UseFormSetValue, useWatch } from 'react-hook-form';
import { flexRender, type Cell, type Row } from '@tanstack/react-table';
import SearchIcon from '@mui/icons-material/Search';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';

import { TABLE_CELL_TYPE } from 'types/enums.ts';
import { computeDisplayColumnsText } from 'utils/helios/displayColumns';
import { TextField } from 'components/mui/common/TextField';
import { Select } from 'components/mui/common/Select';
import { Checkbox } from 'components/mui/common/Checkbox';
import { DatePicker } from 'components/mui/common/DatePicker';
import { TimePicker } from 'components/mui/common/TimePicker';
import { isObjectEmpty } from 'utils/typeChecks';
import { BrowseEditModal } from './cellComponents/helios/modals/BrowseEditModal';

import { useLocaleCodeIsoNorma, useModals } from 'utils/hooks';
import { FileEditModal } from './cellComponents/helios/modals/FileEditModal';
import { type DataSchema, type Schema } from 'types';
import type { HeliosCellData, HeliosCellRawValue } from 'types/helios/agendaListing';
import { roundedToNumber } from 'utils/helios/rounding';
import { ComparisonOperator, ConditionValueSource, HeliosRecordSystemDatasource, LogicalOperator, RoundedToValue } from 'types/dataHelios';
import { getFormFieldName } from './editableComponentFactory';
import { hasRawValue, hasRawValueProps, getCommonPinningStyles } from './tableFunctions';
import { getUIDefinitionIconStyles, mapTextAlignToCss } from '../../../utils/styles.ts';
import { TextArea } from 'components/mui/common/TextArea';
import { LazyRichText as RichText } from 'components/mui/common/LazyRichText';
import { isMobileApp } from 'utils/common';
import { getHeliosCell } from 'utils/table/user/helios/heliosCell';
import type { TableColumnAggregationState } from 'utils/table.ts';
import { formatHeliosNumber, normalizeHeliosNumericSeparators, toHeliosNumericFormValue } from 'utils/helios/numberFormatting';
import { sanitizeNumericInput } from 'utils/validations/sanitizeNumericInput.ts';
import { SearchableBrowseItem } from '../SearchableBrowseItem/SearchableBrowseItem';
import { getSafeHeliosFileCount } from 'utils/helios/fileCount';

const browseCellStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  cursor: 'pointer',
  padding: '8px 12px',
  minWidth: '200px',
  width: '100%',
  border: '1px solid',
  borderColor: 'secondary.main',
  borderRadius: 1,
  '&:hover': {
    '& .MuiTypography-root': {
      textDecoration: 'underline',
    },
    borderColor: 'secondary.main',
  },
} as const;

interface Props<T extends keyof P, P extends DataSchema, K extends FieldValues> {
  row: Row<Schema<T, P>>;
  cell: Cell<Schema<T, P>, unknown>;
  dataSchema: P;
  control: Control<K>;
  setValue: UseFormSetValue<K>;
  isNew: boolean;
  entireRowClicked?: (row: Schema<T, P>) => void;
  /**
   * This prop is needed to trigger a re-render when column aggregation changes.
   * It is not used in the function body, but its presence in the props object ensures that
   * React.memo detects a change and refreshes grouped/aggregated cell output.
   */
  columnAggregation?: TableColumnAggregationState;
}

type ColumnMeta<P extends DataSchema> = {
  uiDefinition: P;
};
const TableCellEditBase = <T extends keyof P, P extends DataSchema, K extends FieldValues>({
  row,
  cell,
  dataSchema,
  control,
  setValue,
  isNew,
  entireRowClicked,
}: Props<T, P, K>) => {
  const { t } = useTranslation();
  const mobileApp = isMobileApp();
  const [isEditing, setIsEditing] = useState(false);
  const [isBrowseModified, setIsBrowseModified] = useState(false);
  const [isFileModified, setIsFileModified] = useState<boolean | number>(false);
  const fileEditDataRef = useRef<{
    dynamicRelationId: number | undefined;
    templateItemId: number | undefined;
    templateViewId: string | undefined;
    recordNumber: number;
    value: HeliosCellRawValue;
  } | null>(null);
  const browseEditDataRef = useRef<{
    codeListTemplateId: string | undefined;
    dynamicRelationId: number | undefined;
    templateItemId: number | undefined;
    templateViewId: string | undefined;
    recordNumber: number;
    value: HeliosCellRawValue;
    valueItemId: number | undefined;
    displayColumns: Array<{ itemId: number; order: number }> | undefined;
    allowMultipleSelection: boolean | undefined;
    dataItems: { itemId: string; value: string }[];
    initialSearch: string;
    isAutoSelect: boolean;
  } | null>(null);

  const cellRef = useRef<HTMLElement | null>(null);

  const locale = useLocaleCodeIsoNorma();
  const currentRowSchema = Object.entries(dataSchema).find(([key]) => key === cell.column.id);
  const cellData = cell.getValue() as HeliosCellData;
  const inputName = getFormFieldName<T, P, K>(cell.row, cell.column.id);
  const uiDefinition = cellData?.uiDefinition ?? (cell.column.columnDef.meta as ColumnMeta<P>)?.uiDefinition;
  const showAsTextArea =
    cellData?.formatting?.formatValue?.typeSpecific?.formatText?.showAsTextArea ??
    currentRowSchema?.[1].formatting?.formatValue?.typeSpecific?.formatText?.showAsTextArea ??
    false;
  const showAsRichText =
    cellData?.formatting?.formatValue?.typeSpecific?.formatText?.showAsRichText ??
    currentRowSchema?.[1].formatting?.formatValue?.typeSpecific?.formatText?.showAsRichText ??
    false;
  const { valueForRender, valueTextAlign, Icon, boxStyles, textStyles } = getHeliosCell({ cell, row, dataSchema });
  const cellTextAlign = mapTextAlignToCss(valueTextAlign);
  const cellTextAlignStyle = { textAlign: cellTextAlign } as const;
  const htmlInputAlignProps = { htmlInput: { style: cellTextAlignStyle } } as const;
  const selectAlignSx = { '& .MuiSelect-select': cellTextAlignStyle } as const;

  const [modals, openModal, closeModal] = useModals({
    browse: false,
    file: false,
  });

  const isRequired = currentRowSchema?.[1].isRequired;
  const componentValue = useWatch({ control, name: inputName });

  const handleOpenBrowseModal = useCallback(
    (searchTerm = '', shouldAutoSelect = false) => {
      const dataItems = Object.keys(dataSchema)
        .filter((key) => {
          const schemaItem = dataSchema[key];
          return schemaItem.templateItemId && schemaItem.templateItemId !== currentRowSchema?.[1].templateItemId;
        })
        .map((key) => {
          const schemaItem = dataSchema[key];
          const rowValue = (row.original as Record<string, unknown>)[key];
          const value =
            typeof rowValue === 'object' && rowValue !== null && 'rawValue' in rowValue ? (rowValue as { rawValue: unknown }).rawValue : rowValue;
          return { itemId: String(schemaItem.templateItemId), value: String(value) };
        });

      browseEditDataRef.current = {
        codeListTemplateId: currentRowSchema?.[1].settings?.browse?.codeListTemplateId,
        dynamicRelationId: currentRowSchema?.[1].settings?.browse?.dynamicRelationId,
        templateItemId: currentRowSchema?.[1].templateItemId,
        templateViewId: currentRowSchema?.[1].templateViewId,
        recordNumber: cellData?.recordNumber,
        value: componentValue ?? cellData?.rawValue,
        valueItemId: currentRowSchema?.[1].settings?.browse?.valueItemId,
        displayColumns: currentRowSchema?.[1].settings?.browse?.displayColumns,
        allowMultipleSelection: currentRowSchema?.[1].settings?.browse?.allowMultipleSelection,
        dataItems,
        initialSearch: searchTerm,
        isAutoSelect: shouldAutoSelect,
      };
      openModal('browse')();
    },
    [currentRowSchema, cellData, componentValue, openModal, dataSchema, row.original]
  );

  const isBrowseType = currentRowSchema?.[1].type === TABLE_CELL_TYPE.HELIOS_BROWSE;
  const isSingleBrowse =
    isBrowseType && !currentRowSchema?.[1].settings?.browse?.dynamicRelationId && !currentRowSchema?.[1].settings?.browse?.allowMultipleSelection;
  const isMultipleBrowse = isBrowseType && !isSingleBrowse;
  const browseDisplayColumns = currentRowSchema?.[1].settings?.browse?.displayColumns;
  const browseCodeListTemplateId = currentRowSchema?.[1].settings?.browse?.codeListTemplateId;

  const browseRecordId = useMemo(() => {
    if (!isBrowseType) return null;
    if (componentValue && typeof componentValue === 'object' && 'id' in componentValue) {
      return componentValue.id;
    }
    if (hasRawValue(cellData) && hasRawValueProps<{ id: number }>(cellData, 'id')) {
      return cellData.rawValue.id;
    }
    return null;
  }, [isBrowseType, componentValue, cellData]);

  const shouldFetchDisplayColumns = Boolean(browseRecordId && browseDisplayColumns && browseCodeListTemplateId && !isNew);

  const browsePrefilterDataItems = useMemo(() => {
    if (!shouldFetchDisplayColumns) return null;

    return Object.keys(dataSchema)
      .filter((key) => {
        const schemaItem = dataSchema[key];
        return schemaItem.templateItemId && schemaItem.templateItemId !== currentRowSchema?.[1].templateItemId;
      })
      .map((key) => {
        const schemaItem = dataSchema[key];
        const rowValue = (row.original as Record<string, unknown>)[key];
        const value =
          typeof rowValue === 'object' && rowValue !== null && 'rawValue' in rowValue ? (rowValue as { rawValue: unknown }).rawValue : rowValue;
        return { itemId: String(schemaItem.templateItemId), value: String(value) };
      });
  }, [shouldFetchDisplayColumns, dataSchema, currentRowSchema, row.original]);

  const browseQueryArgs = useMemo(
    () => ({
      templateId: browseCodeListTemplateId ?? '',
      filters:
        isSingleBrowse && browseRecordId
          ? [
              {
                leftBracket: false,
                rightBracket: false,
                datasource: {
                  templateDataItemId: currentRowSchema?.[1].settings?.browse?.valueItemId ?? 0,
                  systemDatasource: HeliosRecordSystemDatasource.HeliosUser,
                },
                leftLogicalOperator: LogicalOperator.And,
                comparisonOperator: ComparisonOperator.Equals,
                value: {
                  conditionValueSource: ConditionValueSource.None,
                  staticValue: String(browseRecordId),
                  systemDatasource: HeliosRecordSystemDatasource.HeliosUser,
                  templateDataItemId: 0,
                },
              },
            ]
          : [],
      formData: {
        dataItems: browsePrefilterDataItems,
        templateId: currentRowSchema?.[1].templateViewId ?? '',
        itemId: currentRowSchema?.[1].templateItemId ?? null,
        recordId: cellData?.recordNumber,
      },
      searchKeyword: '',
      offset: 0,
      limit: 1,
      orderBy: '',
      orderDesc: false,
      useServiceAccount: false,
    }),
    [browseCodeListTemplateId, isSingleBrowse, browseRecordId, currentRowSchema, browsePrefilterDataItems, cellData?.recordNumber]
  );

  const { data: browseRecordData } = useGetHeliosDataTablesQuery(browseQueryArgs, { skip: !shouldFetchDisplayColumns });

  const browseDisplayText = useMemo(() => {
    if (!isBrowseType) return '';

    // Prefer componentValue (form state) over cellData (server data)
    const formValueName = componentValue && typeof componentValue === 'object' && 'name' in componentValue ? componentValue.name : '';

    const cellDataName =
      hasRawValue(cellData) &&
      hasRawValueProps<{
        name: string;
      }>(cellData, 'name')
        ? cellData.rawValue.name
        : hasRawValue(cellData)
          ? String(cellData.rawValue)
          : '';

    const fallbackName = formValueName || cellDataName;

    if (!browseRecordData?.data || browseRecordData.data.length === 0) return fallbackName;
    const computed = computeDisplayColumnsText(browseRecordData, browseDisplayColumns);
    return computed || fallbackName;
  }, [isBrowseType, browseRecordData, browseDisplayColumns, cellData, componentValue]);

  const getInputTypeFromDataType = useCallback((dataType: string | undefined) => {
    if (!dataType) return 'text';

    switch (dataType) {
      case 'Int32':
      case 'Int64':
      case 'Decimal':
        return 'number';
      case 'String':
        return 'text';
      case 'DateTime':
        return 'dateTime';
      case 'Boolean':
        return 'checkbox';
      default:
        return 'text';
    }
  }, []);

  const fieldBackground = mobileApp ? 'white' : 'transparent';

  const isGrouped = cell.getIsGrouped();
  const isAggregated = cell.getIsAggregated();
  const isPlaceholder = cell.getIsPlaceholder();
  const isRowExpanded = row.getIsExpanded();
  const isGroupingActive = cell.getContext().table.getState().grouping.length > 0;
  const isEditDisabled = isGroupingActive && isAggregated;

  const handleRowExpand = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      row.getToggleExpandedHandler()();
    },
    [row]
  );

  const buildRenderCell = useCallback(() => {
    const renderValue = isAggregated ? cell.getValue() : valueForRender;

    return flexRender(cell.column.columnDef.cell, {
      ...cell.getContext(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getValue: () => renderValue as any,
    });
  }, [cell, isAggregated, valueForRender]);

  const GroupedContent = useCallback(() => {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', overflow: 'hidden', cursor: row.getCanExpand() ? 'pointer' : 'normal' }}
        onClick={handleRowExpand}>
        {isRowExpanded ? (
          <KeyboardArrowDown fontSize="small" sx={{ mr: 0.5, flexShrink: 0 }} />
        ) : (
          <KeyboardArrowRight fontSize="small" sx={{ mr: 0.5, flexShrink: 0 }} />
        )}
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{buildRenderCell()}</Box>
        <Box component="span" sx={{ ml: 0.75, flexShrink: 0 }}>
          ({row.subRows.length})
        </Box>
      </Box>
    );
  }, [row, isRowExpanded, handleRowExpand, buildRenderCell]);

  const getComponentByType = useCallback(() => {
    const schemaUdpType = currentRowSchema?.[1]?.userDefinedAttributeType;
    const hasDateTimeFormatting =
      !!schemaUdpType ||
      !!currentRowSchema?.[1]?.formatting?.formatValue?.typeSpecific?.formatDateTime?.variant ||
      currentRowSchema?.[1]?.dataType === 'DateTime';
    const inputType = hasDateTimeFormatting ? 'dateTime' : getInputTypeFromDataType(currentRowSchema?.[1].dataType);

    switch (currentRowSchema?.[1].type) {
      case TABLE_CELL_TYPE.HELIOS_VALUE:
        if (inputType === 'checkbox') {
          return (
            <Controller
              control={control}
              name={inputName}
              render={({ field }) => <Checkbox {...field} checked={field.value === 'true' || field.value === true} disabled={isEditDisabled} />}
            />
          );
        }
        if (inputType === 'dateTime') {
          const variant = currentRowSchema?.[1]?.formatting?.formatValue?.typeSpecific?.formatDateTime?.variant;
          const isTimeOnly = variant === 'Time' || (!variant && schemaUdpType === 'Time');
          const isDateOnly = variant === 'Date' || variant === 'MonthYear' || (!variant && schemaUdpType === 'Date');

          if (isTimeOnly) {
            return (
              <Controller
                control={control}
                name={inputName}
                render={({ field, fieldState: { error } }) => (
                  <TimePicker
                    {...field}
                    sx={{ width: '100%', minWidth: '10px' }}
                    value={field.value ? new Date(field.value) : null}
                    error={Boolean(error)}
                    formItemUiDefinition={uiDefinition}
                    hideIcon={!isNew}
                    helperText={error?.message}
                    background={fieldBackground}
                    disabled={isEditDisabled}
                  />
                )}
              />
            );
          }

          return (
            <Controller
              control={control}
              name={inputName}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  sx={{ width: '100%', minWidth: '10px' }}
                  value={field.value ? new Date(field.value) : null}
                  error={Boolean(error)}
                  formItemUiDefinition={uiDefinition}
                  helperText={error?.message}
                  format={variant === 'MonthYear' ? 'MM/yyyy' : undefined}
                  {...(isDateOnly ? { views: variant === 'MonthYear' ? ['year', 'month'] : ['year', 'month', 'day'] } : {})}
                  background={fieldBackground}
                  disabled={isEditDisabled}
                />
              )}
            />
          );
        }
        return (
          <Controller
            control={control}
            name={inputName}
            render={({ field, fieldState: { error } }) => {
              const isNumber = inputType === 'number';
              const fieldDataType = currentRowSchema?.[1]?.dataType ?? '';
              const isIntegerField = fieldDataType === 'Int32' || fieldDataType === 'Int64';
              const isEmpty = field.value === '{}' || isObjectEmpty(field.value);
              const rounding =
                roundedToNumber[currentRowSchema?.[1]?.formatting?.formatValue?.typeSpecific?.formatNumber?.roundTo ?? RoundedToValue.Hundreds];
              const formattedValue =
                isNumber &&
                formatHeliosNumber(Number(field.value ?? 0), locale, {
                  minimumFractionDigits: Math.max(0, rounding),
                  maximumFractionDigits: Math.max(0, rounding),
                  thousandsSeparator: currentRowSchema?.[1]?.formatting?.formatValue?.typeSpecific?.formatNumber?.thousandsSeparator,
                  roundTo: currentRowSchema?.[1]?.formatting?.formatValue?.typeSpecific?.formatNumber?.roundTo,
                  mathRound: currentRowSchema?.[1]?.formatting?.formatValue?.typeSpecific?.formatNumber?.mathRound,
                });

              const handleFormattedValue = () => {
                if (isNumber) {
                  setIsEditing(true);
                  setValue(inputName, field?.value);
                }
              };

              if (showAsRichText) {
                const value = isEmpty ? '' : ((field.value as string) ?? '');
                return (
                  <RichText
                    value={value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isEditDisabled}
                    textAlign={cellTextAlign}
                    detail={mobileApp}
                  />
                );
              }

              if (showAsTextArea) {
                const value = isEmpty ? '' : (isNumber ? (isEditing ? field.value : formattedValue) : field.value) || '';
                return (
                  <TextArea
                    {...field}
                    value={value}
                    error={Boolean(error)}
                    helperText={error?.message}
                    required={isRequired}
                    fullWidth
                    maxRows={3}
                    onFocus={handleFormattedValue}
                    onClick={handleFormattedValue}
                    onBlur={() => {
                      setIsEditing(false);
                      if (isIntegerField) {
                        field.onChange(toHeliosNumericFormValue(field.value, fieldDataType));
                      }
                      field.onBlur();
                    }}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    minHeight={null}
                    formItemUiDefinition={uiDefinition}
                    background={fieldBackground}
                    disabled={isEditDisabled}
                    style={cellTextAlignStyle}
                  />
                );
              }

              return (
                <TextField
                  {...field}
                  sx={{ width: '100%', minWidth: mobileApp ? '100%' : '100px' }}
                  value={isEmpty ? '' : ((isNumber ? (isEditing ? field.value : formattedValue) : field.value) ?? '')}
                  type={isNumber ? (isEditing ? 'number' : 'text') : 'text'}
                  error={Boolean(error)}
                  helperText={error?.message}
                  required={isRequired}
                  fullWidth
                  onFocus={handleFormattedValue}
                  onClick={handleFormattedValue}
                  onBlur={() => {
                    setIsEditing(false);
                    if (isIntegerField) {
                      field.onChange(toHeliosNumericFormValue(field.value, fieldDataType));
                    }
                    field.onBlur();
                  }}
                  onChange={(e) => {
                    if (isNumber) {
                      field.onChange(sanitizeNumericInput(normalizeHeliosNumericSeparators(e.target.value)));
                    } else {
                      field.onChange(e.target.value);
                    }
                  }}
                  formItemUiDefinition={uiDefinition}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">{cellData?.unit ?? ''}</InputAdornment>,
                    },
                    htmlInput: {
                      ...htmlInputAlignProps.htmlInput,
                      ...(isNumber ? { inputMode: 'decimal' as const } : {}),
                    },
                  }}
                  background={fieldBackground}
                  disabled={isEditDisabled}
                />
              );
            }}
          />
        );
      case TABLE_CELL_TYPE.HELIOS_STATIC_SELECTION:
        return (
          <Controller
            control={control}
            name={inputName}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                sx={{
                  width: mobileApp ? '100%' : '120px',
                  minWidth: '10px',
                  ...selectAlignSx,
                }}
                onChange={(e) => {
                  field.onChange(e);
                }}
                hideDefaultItem
                error={Boolean(error)}
                helperText={error?.message}
                background={fieldBackground}
                displayEmpty
                formItemUiDefinition={uiDefinition}
                fullWidth
                disabled={isEditDisabled}>
                {currentRowSchema?.[1].settings?.staticSelection.selectItems.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.value}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        );
      case TABLE_CELL_TYPE.HELIOS_BROWSE:
        const displayFormBrowseValue = isSingleBrowse
          ? typeof componentValue === 'object' && componentValue !== null
            ? 'name' in componentValue
              ? componentValue.name
              : 'value' in componentValue
                ? String(componentValue.value)
                : ''
            : String(componentValue || '')
          : Array.isArray(componentValue)
            ? `${t('common.count')}: ${componentValue.filter((item: { status: string }) => item.status === 'add').length}`
            : '';

        const displayValue = browseDisplayText || displayFormBrowseValue;

        if (isMultipleBrowse) {
          return (
            <Box
              onClick={isEditDisabled ? undefined : () => handleOpenBrowseModal()}
              sx={{
                ...browseCellStyles,
                background: fieldBackground,
                minWidth: '10px',
                width: '100%',
                ...(isEditDisabled && { opacity: 0.5, pointerEvents: 'none', backgroundColor: 'action.disabledBackground' }),
                ...boxStyles,
              }}>
              {Icon ? <Icon sx={{ ...getUIDefinitionIconStyles(uiDefinition), flexShrink: 0, marginRight: '4px' }} /> : null}
              <Typography
                variant="body2"
                sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: cellTextAlign, ...textStyles }}>
                {displayValue || '—'}
              </Typography>
              <SearchIcon fontSize="small" sx={{ color: 'action.active', opacity: 0.7 }} />
            </Box>
          );
        }

        return (
          <SearchableBrowseItem
            name={displayValue || ''}
            onSearchSubmit={handleOpenBrowseModal}
            isSingleBrowse={isSingleBrowse}
            disabled={isEditDisabled}
            formItemUiDefinition={uiDefinition}
            onClear={() => {
              setValue(inputName, null as unknown as K[typeof inputName], { shouldDirty: true });
              setIsBrowseModified(true);
            }}
          />
        );
      case TABLE_CELL_TYPE.HELIOS_FILE:
        fileEditDataRef.current = {
          dynamicRelationId: currentRowSchema?.[1].settings?.externalFile?.dynamicRelationId,
          templateItemId: currentRowSchema?.[1].templateItemId,
          recordNumber: cellData?.recordNumber,
          templateViewId: currentRowSchema?.[1].templateViewId,
          value: cellData?.rawValue,
        };
        const isSingleFile = !currentRowSchema?.[1].settings?.externalFile?.dynamicRelationId;
        const displayFormFileValue = `${t('common.count')}: ${getSafeHeliosFileCount(typeof isFileModified === 'number' ? isFileModified : 0)}`;
        const serverFileCount = Math.max(0, cellData?.dynamicRelationValuesCount ?? 0);
        const displayFileValue = isSingleFile
          ? typeof cellData?.rawValue === 'object' && cellData.rawValue !== null && 'name' in cellData.rawValue
            ? cellData.rawValue.name
            : ''
          : `${t('common.count')}: ${serverFileCount}`;
        return (
          <Controller
            control={control}
            name={inputName}
            rules={{
              required: {
                value: isRequired ?? false,
                message: t('common.required'),
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                onClick={isEditDisabled ? undefined : openModal('file')}
                error={Boolean(error)}
                helperText={error?.message}
                sx={{ width: mobileApp ? '100%' : '120px', minWidth: '10px' }}
                value={isNew || typeof isFileModified === 'number' ? displayFormFileValue : displayFileValue}
                background={fieldBackground}
                disabled={isEditDisabled}
                formItemUiDefinition={uiDefinition}
              />
            )}
          />
        );
      case TABLE_CELL_TYPE.HELIOS_LINK:
        return (
          <Controller
            control={control}
            name={inputName}
            render={({ field, fieldState: { error } }) => (
              <TextField
                sx={{ width: mobileApp ? '100%' : '100px', minWidth: '10px' }}
                {...field}
                value={field.value === '{}' || isObjectEmpty(field.value) ? '' : field.value}
                type="number"
                error={Boolean(error)}
                helperText={error?.message}
                required={isRequired}
                formItemUiDefinition={uiDefinition}
                fullWidth
                background={fieldBackground}
                disabled={isEditDisabled}
                slotProps={htmlInputAlignProps}
              />
            )}
          />
        );
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getInputTypeFromDataType,
    currentRowSchema,
    control,
    inputName,
    cellData,
    componentValue,
    t,
    handleOpenBrowseModal,
    openModal,
    isNew,
    isBrowseModified,
    isFileModified,
    locale,
    isEditing,
    isRequired,
    setValue,
    browseDisplayText,
    isSingleBrowse,
    uiDefinition,
    isEditDisabled,
    cellTextAlign,
    showAsRichText,
  ]);

  const handleEntireRowClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (e.target === cellRef.current) {
        entireRowClicked?.(row.original);
      }
    },
    [entireRowClicked, row]
  );

  const renderCell = () => {
    switch (true) {
      case isGrouped:
        return <GroupedContent />;
      case isAggregated:
        return buildRenderCell();
      case isPlaceholder:
        return null;
      default:
        return getComponentByType();
    }
  };

  const cellComponent =
    showAsTextArea || showAsRichText ? (
      <MuiTableCell
        ref={cellRef}
        key={cell.id}
        onClick={handleEntireRowClick}
        sx={{
          paddingBottom: '10px',
          paddingLeft: '4px',
          paddingRight: '4px',
          verticalAlign: 'top',
          width: `calc(var(--col-${CSS.escape(cell.column.id)}-size) * 1px)`,
          maxWidth: `calc(var(--col-${CSS.escape(cell.column.id)}-size) * 1px)`,
          overflow: 'hidden',
          boxSizing: 'border-box',
          ...getCommonPinningStyles(cell.column, { id: cell.row.id }),
          ...(mobileApp && { display: 'block', padding: 0, border: 0, width: '100%' }),
        }}>
        {renderCell()}
      </MuiTableCell>
    ) : (
      <MuiTableCell
        ref={cellRef}
        key={cell.id}
        onClick={handleEntireRowClick}
        sx={{
          paddingLeft: '4px',
          paddingRight: '4px',
          verticalAlign: 'top',
          width: `calc(var(--col-${CSS.escape(cell.column.id)}-size) * 1px)`,
          maxWidth: `calc(var(--col-${CSS.escape(cell.column.id)}-size) * 1px)`,
          overflow: 'hidden',
          boxSizing: 'border-box',
          ...getCommonPinningStyles(cell.column, { id: cell.row.id }),
          ...(mobileApp ? { display: 'block', padding: 0, border: 0, width: '100%' } : {}),
        }}>
        {renderCell()}
      </MuiTableCell>
    );
  return (
    <>
      {cellComponent}
      <BrowseEditModal
        open={modals.browse}
        onClose={closeModal('browse')}
        codeListTemplateId={browseEditDataRef.current?.codeListTemplateId}
        dynamicRelationId={browseEditDataRef.current?.dynamicRelationId}
        templateItemId={browseEditDataRef.current?.templateItemId}
        templateViewId={browseEditDataRef.current?.templateViewId}
        recordNumber={browseEditDataRef.current?.recordNumber}
        value={browseEditDataRef.current?.value}
        setValue={setValue}
        changeValue={setIsBrowseModified}
        fieldName={inputName}
        isNew={isNew}
        valueItemId={browseEditDataRef.current?.valueItemId}
        displayColumns={browseEditDataRef.current?.displayColumns}
        allowMultipleSelection={browseEditDataRef.current?.allowMultipleSelection}
        dataItems={browseEditDataRef.current?.dataItems}
        initialSearch={browseEditDataRef.current?.initialSearch ?? ''}
        isAutoSelectSearchResultEnabled={browseEditDataRef.current?.isAutoSelect ?? false}
      />
      <FileEditModal
        open={modals.file}
        onClose={closeModal('file')}
        dynamicRelationId={fileEditDataRef.current?.dynamicRelationId}
        templateItemId={fileEditDataRef.current?.templateItemId}
        templateViewId={fileEditDataRef.current?.templateViewId}
        recordNumber={fileEditDataRef.current?.recordNumber}
        value={fileEditDataRef.current?.value}
        setValue={setValue}
        changeValue={setIsFileModified}
        fieldName={inputName}
        isNew={isNew}
      />
    </>
  );
};

export const TableCellEditHelios = memo(TableCellEditBase) as unknown as typeof TableCellEditBase;
